/**
 * scrPanelState.ts — what the clinician review's SCR panel shows, as typed states.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: returning patients S7.1 (2026-09-25).
 *
 * WHY. The panel used to turn EVERY failed SCR read into "Sorry — we could not
 * load the SCR details." with a Try again button. The commonest failure was not
 * a fault: a patient who paid without finishing their account has no identity
 * record, so identity answers 404, and clinicians were shown a system error for
 * a normal situation. The CTO's decision (Q4): a clear explanation, never an
 * error look.
 *
 * The states:
 *  - `not_completed`: the patient has no account yet. Identity's future 409
 *    `account_not_completed` (plan S2.3), OR today's 404 when the order is
 *    still held by a checkout guest principal (lib/guestPrincipal.ts).
 *  - `completed_unverified`: they made an account but have not confirmed the
 *    email. Only the future 409 can say this.
 *  - `no_record`: a 404 for a REAL account id. Not "no account yet" (they have
 *    one) and not something Try again can fix, so it gets the same calm line as
 *    a case with no patient id at all.
 *  - `failed`: everything else. Retryable only for a 5xx or a network failure,
 *    because only those can come out differently a second later. A 401 has
 *    already signed the user out (api/client.ts); a 400, 403 or an unknown 409
 *    will answer the same way every time, so offering Try again would be a
 *    button that never works.
 *
 * Pure, so the whole mapping is pinned by scrPanelState.spec.ts without a
 * render.
 */
import { ApiError } from '../api/client';
import type { ScrUnavailableReason } from '../api/identity';
import type { ScrBlock } from '../api/types';
import { isGuestShapedPrincipalId } from '../lib/guestPrincipal';

export type ScrPanelState =
  | { kind: 'loading' }
  /** No patient id on the order, so no read was made. */
  | { kind: 'skip' }
  | { kind: 'ready'; scr: ScrBlock }
  | { kind: ScrUnavailableReason }
  | { kind: 'no_record' }
  | { kind: 'failed'; retryable: boolean };

/**
 * The Q4 copy, VERBATIM from the CTO's decision (PLAN §2.2 / DECISIONS Q4).
 * scrPanel.spec.tsx pins it as a literal; do not reword it here.
 */
export const SCR_NOT_COMPLETED_COPY =
  "This patient hasn't created their account yet, so their Summary Care Record can't be looked up. It will be available once they complete their account.";

/** The unconfirmed-email variant, VERBATIM (PLAN §2.2, the CTO's 17:00 safe defaults). */
export const SCR_COMPLETED_UNVERIFIED_COPY =
  "This patient has created their account but hasn't confirmed their email yet, so their Summary Care Record can't be looked up. It will be available once they confirm it.";

/** The calm line for "there is no patient record to read" (no id, or a 404 on an account). */
export const SCR_NO_RECORD_COPY = 'No linked patient record for this case yet, so there are no SCR details to copy.';

/** The one error line, shown only for real failures. */
export const SCR_FAILED_COPY = 'Sorry — we could not load the SCR details.';

/**
 * Map a failed SCR read to the state the panel shows. `patientUserId` is the
 * id the read was made for (the order's patient).
 */
export function scrStateFromError(error: unknown, patientUserId: string): ScrPanelState {
  // A rejected fetch (offline, DNS, CORS, a dropped connection) never became
  // an HTTP answer at all. The client only throws ApiError for HTTP statuses.
  if (!(error instanceof ApiError)) return { kind: 'failed', retryable: true };
  // A server or gateway failure can come out differently a second later,
  // whatever its body says.
  if (error.status >= 500) return { kind: 'failed', retryable: true };

  // identity's `account_not_completed` code is the authority when it is sent.
  // Read on any 4xx rather than only on the planned 409: the code exists for
  // this one situation, and pinning the status as well would turn a
  // status-only change on the identity side into a false error screen.
  if (error.reason === 'not_completed' || error.reason === 'completed_unverified') {
    return { kind: error.reason };
  }
  if (error.status === 404) {
    return isGuestShapedPrincipalId(patientUserId) ? { kind: 'not_completed' } : { kind: 'no_record' };
  }
  return { kind: 'failed', retryable: false };
}
